import Cleave from 'cleave.js';
import $ from 'jquery';
import uuid from 'uuid';

import inject from '@rover/utilities/injector';
import retry from '@rover/utilities/retry';

import 'cleave.js/dist/addons/cleave-phone.i18n';
import 'intl-tel-input';
import 'intl-tel-input/build/css/intlTelInput.css';

const defaultLocale = 'en-us';
const missingModuleMessage = 'Cannot find module';
const EU_COUNTRIES = ['gb', 'de', 'es', 'fr', 'it', 'nl', 'no', 'se'];
export const DEFAULT_PREFERRED_COUNTRIES = [
  'us',
  'ca',
  'gb',
  'de',
  'es',
  'fr',
  'it',
  'nl',
  'no',
  'se',
];

function localizedIntlTelInput($intlTelInput, utilsLoadedCallback, localizedCountries) {
  $intlTelInput.each(function () {
    // hack: for django form to make sure the correct value is sent to django b/c we cannot call js using
    // traditional post. This will not be an issue if the flow use ajax
    const name = $(this).attr('name');
    $(this).attr('name', '');
    // the utils script will be load lazily. Temp solution until we get better phone number input component
    $(this)
      .intlTelInput({
        utilsScript: 'https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/13.0.0/js/utils.js',
        hiddenInput: name,
        autoPlaceholder: 'off',
        preferredCountries: DEFAULT_PREFERRED_COUNTRIES,
        localizedCountries,
        initialCountry: 'auto',
        geoIpLookup: (callback) => {
          callback(inject('Rover.context.countryCode', 'US'));
        },
      })
      .done(() => {
        if (utilsLoadedCallback) {
          utilsLoadedCallback($(this));
        }
      });

    updateAccessibilityAttributes($(this));

    $(this).on('countrychange', (e, countryData) => {
      if (typeof window.intlTelInputUtils === 'undefined') return;

      const newNumber = window.intlTelInputUtils.formatNumber(
        $(this).val(),
        countryData.iso2,
        window.intlTelInputUtils.numberFormat.INTERNATIONAL
      );
      $(this).intlTelInput('setNumber', newNumber);
    });
  });
  return $intlTelInput;
}

export function initIntlTelInput(inputEl, utilsLoadedCallback) {
  const $intlTelInput = inputEl ? $(inputEl) : $('.js-intl-tel-input');

  const loadStrings = (language) =>
    retry(async () => {
      let countries = {};
      try {
        countries = await import(`../../locale/${language}/countries.json`);
      } catch (error) {
        // We expect this to fail if locale doesn't have countries defined
        // and we fall back to the US list. In all other cases, we should retry
        if (!error.message.includes(missingModuleMessage)) {
          throw error;
        }
      }
      return countries;
    });
  const locale = inject('Rover.context.language', defaultLocale);

  if (locale !== defaultLocale) {
    loadStrings(locale).then(
      (localizedCountries) =>
        localizedIntlTelInput($intlTelInput, utilsLoadedCallback, localizedCountries),
      () => localizedIntlTelInput($intlTelInput, utilsLoadedCallback, {})
    );
  } else {
    localizedIntlTelInput($intlTelInput, utilsLoadedCallback, {});
  }

  return $intlTelInput;
}

function getCleaveOptions(countryCode) {
  switch (countryCode) {
    case 'us':
    case 'ca':
      return {
        blocks: [3, 3, 4],
        delimiter: '-',
        numericOnly: true,
      };
    default:
      return {
        phone: true,
        phoneRegionCode: countryCode,
      };
  }
}

export function updateCleaveForInputs() {
  // For each of the input, initialize cleave. Cleave gets destroyed and recreated when user change country
  const countryCode = $(this).intlTelInput('getSelectedCountryData').iso2;
  if (!countryCode) return;

  const cleave = $(this).data('cleave');
  if (cleave) {
    cleave.destroy();
  }
  const value = $(this).val() || '';

  // hack: strip out +XX otherwise cleave will keep adding it in the beginning
  if (['us', 'ca'].includes(countryCode)) {
    if (value.startsWith('+1')) {
      $(this).val(value.substring(2));
    }
  }
  if (EU_COUNTRIES.includes(countryCode)) {
    const intlCodeRegex = /^\+\d{2}/;
    if (intlCodeRegex.test(value)) {
      $(this).val(value.substring(3));
    }
  }

  $(this).data('cleave', new Cleave(this, getCleaveOptions(countryCode)));
}

function updateAccessibilityAttributes($intlTelInput) {
  // These bring the input up to speed with modern accessibility guidelines.
  // This whole function can likely be removed once our intl-tel-input version is upgraded to the latest.
  // (See https://roverdotcom.atlassian.net/browse/DEV-56864)
  const $flagControl = $intlTelInput.siblings('.flag-container').children('.selected-flag');
  const $countryDropdown = $flagControl.siblings('.country-list');
  $flagControl.attr('aria-expanded', false);
  $intlTelInput.on('open:countrydropdown', () => {
    $flagControl.attr('aria-expanded', true);
  });
  $intlTelInput.on('close:countrydropdown', () => {
    $flagControl.attr('aria-expanded', false);
  });

  // Create unique IDs for each input's elements (current version of intl-tel-input doesn't do this)
  const uniqueId = uuid();
  const newId = `country-listbox-${uniqueId}`;
  $countryDropdown.attr('id', newId);
  $flagControl.attr('aria-owns', `${newId} ${$intlTelInput.attr('id')}`);
  $intlTelInput.attr('role', 'textbox');

  $countryDropdown.children('.country.preferred').each((i, el) => {
    $(el).attr('id', `${$(el).attr('id')}-${uniqueId}`);
  });
  $countryDropdown.attr(
    'aria-activedescendant',
    `${$countryDropdown.attr('aria-activedescendant')}-${uniqueId}`
  );
}



// WEBPACK FOOTER //
// ./js/components/intl_tel_input.js